// A saved filter -> the query string its own list reads.
//
// Filters saved since 2026-10-15 carry `query`, the list's exact parameters.
// Older issue presets carry only the toolbar's fields, so those are spelled
// into the same parameters here - the cost report must cover the issues the
// Issues Manager listed when the filter was used, not a guess at them.
// A shop drawing filter is one project's (the drawings list is per project),
// but the list's own query never named it - so with no project picked on the
// cost page the server could not tell which project to read and refused
// (owner, 2026-10-16: the page went white). The preset knows its project.
function withProject(query, preset, kind) {
  if (kind !== "shopDrawings" || !preset.projectId || /(^|&)projectId=/.test(query)) return query;
  return `${query}${query ? "&" : ""}projectId=${encodeURIComponent(preset.projectId)}`;
}

export function savedFilterQuery(preset, kind) {
  if (!preset) return "";
  if (preset.query) return withProject(String(preset.query), preset, kind);
  const qs = new URLSearchParams();
  const put = (k, v) => { if (v !== undefined && v !== null && v !== "" && v !== false) qs.set(k, v === true ? "true" : String(v)); };
  if (kind === "shopDrawings") {
    qs.set("filters", "1");
    for (const [k, v] of Object.entries(preset.filters || {})) put(k, Array.isArray(v) ? v.join(",") : v);
    return withProject(qs.toString(), preset, kind);
  }
  const status = Array.isArray(preset.statusFilter) ? preset.statusFilter.join(",") : preset.statusFilter;
  const priority = Array.isArray(preset.priorityFilter) ? preset.priorityFilter.join(",") : preset.priorityFilter;
  put("status", status);
  put("priority", priority);
  put("search", preset.searchValue);
  put("hideAbandoned", !!preset.hideAbandoned);
  put("hideResolved", !!preset.hideResolved);
  put("hideUnderReview", !!preset.hideUnderReview);
  put("createdFrom", preset.createdRange && preset.createdRange.from);
  put("createdTo", preset.createdRange && preset.createdRange.to);
  put("dueFrom", preset.dueRange && preset.dueRange.from);
  put("dueTo", preset.dueRange && preset.dueRange.to);
  put("titleTag", preset.titleTagFilter);
  put("createdBy", preset.createdByFilter);
  put("updatedBy", preset.updatedByFilter);
  put("ownerSignOff", preset.ownerSignOffFilter);
  if (preset.areaSignOffFilter) {
    put("areaSignOff", preset.areaSignOffFilter);
    put("areaStage", preset.areaStage);
    put("areaSignOffAsOf", preset.areaAsOf);
    put("areaSignOffSource", preset.areaSource);
  }
  return qs.toString();
}

// ---- WHICH SAVED FILTERS BELONG TO THE PROJECT ON SCREEN -----------------
//
// Owner, 2026-10-22: "fix the filter creation and selection on the
// contractor hours and cost, and make sure each filter shows up on the
// correct project, and not mix filters between projects".
//
// Both preset lists are ONE per-user preference - issues.savedFilters and
// shopDrawings.savedFilters - and this page offered every one of them
// whatever project was picked. A shop drawing filter saved on Apple RT06
// was on the menu while you were costing SpaceX BA03, and applying it asked
// the server for another project's drawings.
//
// Each preset already records the project it was saved on. The issue list
// can be filtered across every project, so an issue preset saved with no
// project is a genuine ALL-PROJECTS filter and stays on the menu - labelled,
// so "every project" is something you read rather than infer.

const pid = (v) => (v == null ? "" : String(v));

/**
 * The presets to offer for `projectId`, newest rules first:
 *   - saved on this project            -> offered
 *   - saved with no project (issues)   -> offered, marked allProjects
 *   - saved on another project         -> left out when a project is picked
 *
 * With NO project picked nothing can be mixed into the wrong project, so
 * everything is offered and each row says which project it came from.
 *
 * `names` maps a project id to its title, for that label.
 */
export function presetsForProject(presets, projectId, { names } = {}) {
  const want = pid(projectId);
  const titleOf = (id) => (names && names[pid(id)]) || "";
  const out = [];
  for (const p of presets || []) {
    if (!p || !p.name) continue;
    const on = pid(p.projectId);
    const allProjects = !on;
    if (want && !allProjects && on !== want) continue;
    out.push({
      ...p,
      allProjects,
      // what the row reads as: the bare name on its own project, and the
      // project named whenever that is not obvious
      label: allProjects
        ? `${p.name} (every project)`
        : (want ? p.name : `${p.name}${titleOf(on) ? ` — ${titleOf(on)}` : ""}`),
    });
  }
  return out;
}

/**
 * The preset a name stands for, ON THIS PROJECT.
 *
 * It used to be find(f => f.name === name). Two projects each with a filter
 * called "Open electrical" is not unusual, and that match took whichever was
 * saved first - so the page could apply one project's filter while naming
 * another's. The project is part of the identity, not decoration.
 */
export function findPreset(presets, name, projectId) {
  if (!name) return null;
  const want = pid(projectId);
  const byName = (presets || []).filter((p) => p && p.name === name);
  if (!byName.length) return null;
  return byName.find((p) => pid(p.projectId) === want)
    || byName.find((p) => !pid(p.projectId))
    || (want ? null : byName[0]);
}
